// [PURPOSE]: Save-side and load-side helpers that move image dataUrls between
//   React state and the IDB-backed blob store. The persisted JSON payload
//   contains only short ID tokens — actual dataUrls live in IDB.
//
// [DESIGN]: The two functions are inverses. `stripImagesFromState` mutates a
//   shallow copy of state, lifts dataUrls into IDB, and replaces them with
//   tokens. `restoreImagesIntoState` walks the same paths in reverse, looking
//   up tokens in the in-memory cache (which was hydrated at app startup).
//
// [TOKEN FORMAT]: `img:<id>` for portraits and item icons. Gallery entries use
//   the existing object shape `{ id, type, prompt, createdAt }` and just drop
//   the `data` field; the lookup is implicit (record.id is the IDB key).
//
// [FIELDS HANDLED]:
//   - character.basics.portrait        → string (dataUrl) → 'img:<id>'
//   - character.portrait               → legacy duplicate of above
//   - character.imageGallery[i].data   → dataUrl → dropped (id remains)
//   - inventory[i].customImage         → string (dataUrl) → 'img:<id>'
//
// [INTEGRATION]: Called from CharacterContext / App.jsx around the autosave
//   write and the load-character read path.

import {
  putImage,
  getCachedImage,
  deriveStableId,
} from './imageBlobStore';

const TOKEN_PREFIX = 'img:';
const DATA_PREFIX = 'data:';

function isDataUrl(value) {
  return typeof value === 'string' && value.startsWith(DATA_PREFIX);
}

function isToken(value) {
  return typeof value === 'string' && value.startsWith(TOKEN_PREFIX);
}

function tokenToId(token) {
  return token.slice(TOKEN_PREFIX.length);
}

function idToToken(id) {
  return `${TOKEN_PREFIX}${id}`;
}

/**
 * [PURPOSE]: Lift any dataUrl-shaped portrait/customImage into IDB, replacing
 *   it with a compact `img:<id>` token. Called before persisting state to
 *   localStorage / Firestore. Idempotent: tokens pass through untouched.
 *
 * [SHAPE]: Takes the FULL playerStats object and the inventory array
 *   separately, mirroring how App.jsx persists them. Returns
 *   { playerStats, inventory } shallow-cloned with image fields rewritten.
 *
 * [NOTE]: Asynchronous because IDB writes are async. Callers must `await`.
 */
export async function stripImagesFromState(playerStats, inventory) {
  const writes = [];

  let nextStats = playerStats;
  if (playerStats && typeof playerStats === 'object') {
    nextStats = { ...playerStats };

    // Portrait: basics.portrait + legacy character.portrait
    const basics = nextStats.basics ? { ...nextStats.basics } : null;
    if (basics && isDataUrl(basics.portrait)) {
      const id = deriveStableId(basics.portrait);
      writes.push(putImage(id, basics.portrait, { type: 'portrait', createdAt: new Date().toISOString() }));
      basics.portrait = idToToken(id);
    }
    if (basics) nextStats.basics = basics;

    if (isDataUrl(nextStats.portrait)) {
      // If basics.portrait was set above, mirror its token. Otherwise mint new.
      if (basics && isToken(basics.portrait)) {
        nextStats.portrait = basics.portrait;
      } else {
        const id = deriveStableId(nextStats.portrait);
        writes.push(putImage(id, nextStats.portrait, { type: 'portrait', createdAt: new Date().toISOString() }));
        nextStats.portrait = idToToken(id);
      }
    }

    // Image gallery: keep entry shape, drop `data`, push to IDB keyed by id
    if (Array.isArray(nextStats.imageGallery) && nextStats.imageGallery.length > 0) {
      nextStats.imageGallery = nextStats.imageGallery.map((entry) => {
        if (!entry || typeof entry !== 'object') return entry;
        if (isDataUrl(entry.data) && entry.id) {
          writes.push(putImage(entry.id, entry.data, {
            type: entry.type || 'unknown',
            prompt: entry.prompt || '',
            createdAt: entry.createdAt || new Date().toISOString(),
          }));
          // Strip data from persisted payload — IDB is source of truth
          const { data, ...rest } = entry;
          return rest;
        }
        return entry;
      });
    }
  }

  let nextInventory = inventory;
  if (Array.isArray(inventory)) {
    nextInventory = inventory.map((item) => {
      if (!item || typeof item !== 'object') return item;
      if (isDataUrl(item.customImage)) {
        const id = deriveStableId(item.customImage);
        writes.push(putImage(id, item.customImage, {
          type: 'icon',
          prompt: item.name || '',
          createdAt: new Date().toISOString(),
        }));
        return { ...item, customImage: idToToken(id) };
      }
      return item;
    });
  }

  // Wait for IDB writes to settle. Failures are already swallowed by putImage
  //   (it falls back to localStorage internally), so this should not throw.
  if (writes.length > 0) {
    await Promise.allSettled(writes);
  }

  return { playerStats: nextStats, inventory: nextInventory };
}

/**
 * [PURPOSE]: Inverse of stripImagesFromState — replaces `img:<id>` tokens
 *   with their dataUrl from the cache. Synchronous: assumes hydrateAll() has
 *   already been awaited at app startup.
 *
 * [TOLERANCE]: If a token has no cache hit, returns null for the field
 *   instead of throwing. Components already handle null portraits/customImages.
 */
export function restoreImagesIntoState(playerStats, inventory) {
  let nextStats = playerStats;
  if (playerStats && typeof playerStats === 'object') {
    nextStats = { ...playerStats };

    if (nextStats.basics && isToken(nextStats.basics.portrait)) {
      const dataUrl = getCachedImage(tokenToId(nextStats.basics.portrait));
      nextStats.basics = { ...nextStats.basics, portrait: dataUrl || null };
    }
    if (isToken(nextStats.portrait)) {
      const dataUrl = getCachedImage(tokenToId(nextStats.portrait));
      nextStats.portrait = dataUrl || null;
    }

    if (Array.isArray(nextStats.imageGallery) && nextStats.imageGallery.length > 0) {
      nextStats.imageGallery = nextStats.imageGallery.map((entry) => {
        if (!entry || typeof entry !== 'object') return entry;
        if (entry.data) return entry; // already inflated
        if (entry.id) {
          const dataUrl = getCachedImage(entry.id);
          if (dataUrl) return { ...entry, data: dataUrl };
        }
        return entry;
      });
    }
  }

  let nextInventory = inventory;
  if (Array.isArray(inventory)) {
    nextInventory = inventory.map((item) => {
      if (!item || typeof item !== 'object') return item;
      if (isToken(item.customImage)) {
        const dataUrl = getCachedImage(tokenToId(item.customImage));
        return { ...item, customImage: dataUrl || null };
      }
      return item;
    });
  }

  return { playerStats: nextStats, inventory: nextInventory };
}

/**
 * [PURPOSE]: For exporting a save bundle (where the user wants images
 *   preserved across machines), inline tokens back to dataUrls. Used by the
 *   "Export character" button so the .json file is self-contained.
 */
export function inlineImagesForExport(playerStats, inventory) {
  return restoreImagesIntoState(playerStats, inventory);
}

// Exposed for tests + future export bundling.
export const __TOKEN_PREFIX__ = TOKEN_PREFIX;
export const __isToken__ = isToken;
export const __tokenToId__ = tokenToId;
